import { morph, MORPH_OPTIONS } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

/**
 * Elemente, die beim Modellwechsel vom gewählten Produkt übernommen werden. Alles andere
 * (Titel, Beschreibung, weitere Blöcke) bleibt beim Produkt der Seite.
 * Bei Änderungen auch die Liste zu `data-amq-model-pending` im CSS des Blocks anpassen.
 */
const SWAPPED_ELEMENTS = [
  'media-gallery', // Produktbild und Galerie
  'product-price',
  'variant-picker',
  'product-form-component', // Varianten-ID und Verfügbarkeit im Warenkorbformular
  'product-inventory',
  'product-sku-component',
  'sticky-add-to-cart',
  'amq-product-variant-weight', // Gewicht der Variante im Aufbau-Akkordeon (blocks/amq_product_variant_weight.liquid)
];

/** Auswahl-Elemente der Variantenauswahl */
const PICKER_CONTROLS = 'variant-picker input[type="radio"], variant-picker select option';

/**
 * Versteckte Positionseigenschaft (Unterstrich = weder im Warenkorb noch an der Kasse sichtbar): Pfad der
 * Seite, auf der ein fremdes Modell in den Warenkorb gelegt wurde. Der Warenkorb verlinkt die Position
 * dorthin mit `?variant=` (snippets/cart-products.liquid), der Switcher öffnet dort das Modell wieder
 * (#selectFromUrl).
 */
const PAGE_PROPERTY = 'properties[_amq_seite]';
/** Nur noch für ältere Warenkorb-Links (#restoreFromUrl) */
const MODEL_PARAM = 'amq_modell';
const VARIANT_PARAM = 'amq_variante';

/** Wie lange eine vorgeladene Section wiederverwendet wird (Preise/Verfügbarkeit können sich ändern) */
const CACHE_TTL_MS = 2 * 60 * 1000;

/**
 * AMQ Modell-Switcher
 *
 * Wechselt auf der Produktseite zwischen verknüpften Produkten (Metafeld „Modellauswahl“),
 * ohne den URL-Pfad zu ändern: Die Section des gewählten Produkts wird über die Section Rendering API
 * geladen, und nur Galerie, Preis, Variantenauswahl und Warenkorbformular werden ausgetauscht.
 * Beim Hovern/Fokussieren eines Modells wird dessen Section vorgeladen.
 *
 * `?variant=` folgt der Auswahl, auch wenn die Variante zu einem anderen Modell gehört. Shopify
 * ignoriert solche fremden Variant-IDs und rendert das Produkt der Seite; der Switcher lädt dann
 * beim Start das passende Modell nach (#selectFromUrl). So lassen sich Varianten aller Modelle
 * verlinken, und Neuladen oder Teilen zeigt dieselbe Auswahl.
 *
 * Nach jeder Variantenwahl gleicht der Switcher die gewählten Optionen mit den Varianten der anderen
 * Modelle ab: Gibt es die Kombination dort nicht (oder ist sie ausverkauft), wird das Modell
 * deaktiviert; sonst zeigt es den Preis dieser Variante und öffnet sie beim Klick direkt.
 */
class AmqModelSwitcher extends HTMLElement {
  /**
   * Antworttexte je Request-URL. Gespeichert wird der Text, nicht das geparste Dokument,
   * weil morph() Knoten aus dem neuen Baum übernimmt und ein Dokument so nur einmal nutzbar ist.
   * @type {Map<string, { time: number, text: Promise<string> }>}
   */
  #cache = new Map();

  /** Zähler, um Antworten älterer Klicks zu verwerfen */
  #requestCount = 0;

  /** @type {string | undefined} */
  #pageStickyTitle;

  /** @type {Promise<void> | undefined} */
  #pendingVariantUpdate;

  /**
   * Varianten der Modelle aus dem JSON im Block, je Produkt-ID
   * @type {Map<string, ModelData>}
   */
  #models = new Map();

  connectedCallback() {
    this.#readModelData();
    this.#adoptPreload();
    this.#pageStickyTitle = this.#section?.querySelector('.sticky-add-to-cart__title')?.textContent ?? undefined;
    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointerenter', this.#onIntent, { capture: true });
    this.addEventListener('focusin', this.#onIntent);
    this.#section?.addEventListener('change', this.#onSectionChange, { capture: true });
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#updateAvailability();
    this.#restoreFromUrl();
    this.#selectFromUrl();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('pointerenter', this.#onIntent, { capture: true });
    this.removeEventListener('focusin', this.#onIntent);
    this.#section?.removeEventListener('change', this.#onSectionChange, { capture: true });
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  get #section() {
    return this.closest('.shopify-section');
  }

  get #activeProductId() {
    return this.querySelector('[aria-pressed="true"]')?.getAttribute('data-product-id') ?? this.dataset.pageProductId;
  }

  /**
   * Merkt sich laufende Variantenwechsel des Variant-Pickers, damit ein Modellwechsel darauf wartet.
   * Sonst kann die (langsamere) Antwort des Pickers nach dem Modellwechsel eintreffen und Auswahl,
   * Preis und Warenkorbformular wieder mit dem vorherigen Modell überschreiben.
   * Außerdem: Die Sticky-Leiste morpht sich bei jedem Variantenwechsel mit dem HTML des geladenen
   * Produkts – danach den Titel wieder auf das Seitenprodukt setzen.
   * @param {Event & { promise?: Promise<unknown> }} event
   */
  #onProductSelect = (event) => {
    if (!event.promise) return;

    const settled = event.promise.then(
      () => {},
      () => {}
    );
    this.#pendingVariantUpdate = settled;

    settled.then(() => {
      if (this.#pendingVariantUpdate === settled) this.#pendingVariantUpdate = undefined;
      setTimeout(() => this.#restoreStickyTitle());
    });
  };

  /** Wartet, bis kein Variantenwechsel des Pickers mehr läuft */
  async #waitForVariantUpdates() {
    while (this.#pendingVariantUpdate) {
      await this.#pendingVariantUpdate;
      // Die Komponenten (Preis, Formular, Sticky-Leiste) aktualisieren sich in eigenen Callbacks
      await new Promise((resolve) => setTimeout(resolve));
    }
  }

  #restoreStickyTitle(root = this.#section) {
    const title = root?.querySelector('.sticky-add-to-cart__title');
    if (title && this.#pageStickyTitle !== undefined && title.textContent !== this.#pageStickyTitle) {
      title.textContent = this.#pageStickyTitle;
    }
  }

  /**
   * Gleicht nach jeder Variantenwahl die anderen Modelle neu ab. `?variant=` schreibt der
   * Variant-Picker selbst, auch für ein anderes Modell als das der Seite (siehe #selectFromUrl).
   * @param {Event} event
   */
  #onSectionChange = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('variant-picker')) return;

    // Der Variant-Picker setzt die neue Auswahl erst in seinem eigenen Listener
    setTimeout(() => this.#updateAvailability());
  };

  /**
   * Übernimmt die Section, die das Inline-Script im Block bei einem Deep-Link schon während des
   * Seitenaufbaus angefragt hat. Passt ihre URL nicht zu #buildRequestUrl, wird sie nie verwendet.
   */
  #adoptPreload() {
    const element = /** @type {this & { amqPreload?: { url: string, text: Promise<string> } }} */ (this);
    const preload = element.amqPreload;
    if (!preload) return;

    delete element.amqPreload;
    this.#cache.set(preload.url, { time: Date.now(), text: preload.text });
    preload.text.catch(() => this.#cache.delete(preload.url));
  }

  /**
   * Deep-Link: Gehört `?variant=` zu einem anderen Modell als dem der Seite, dieses Modell mit genau
   * dieser Variante laden. Bis dahin blendet `data-amq-model-pending` an <html> die ausgetauschten
   * Elemente aus (snippets/amq-model-switcher-early.liquid), damit das Seitenprodukt nicht aufblitzt.
   */
  #selectFromUrl() {
    const variantId = new URL(window.location.href).searchParams.get('variant');
    const model = variantId
      ? [...this.#models.values()].find(
          (candidate) =>
            String(candidate.id) !== this.dataset.pageProductId &&
            candidate.variants.some((variant) => String(variant.id) === variantId)
        )
      : undefined;
    const button = model ? this.querySelector(`button[data-product-id="${model.id}"]`) : null;

    if (!variantId || !(button instanceof HTMLButtonElement)) {
      document.documentElement.removeAttribute('data-amq-model-pending');
      return;
    }

    button.dataset.variantId = variantId;
    this.#select(button);
  }

  /**
   * Ältere Warenkorb-Links (`?amq_modell=…&amq_variante=…`): Modell und Variante wieder auswählen.
   * Neue Links nutzen `?variant=` (#selectFromUrl). Die Parameter werden aus der URL entfernt; nach dem
   * Laden steht dort wie bei jedem Modellwechsel `?variant=` (#select).
   */
  #restoreFromUrl() {
    const url = new URL(window.location.href);
    const modelId = url.searchParams.get(MODEL_PARAM);
    if (!modelId) return;

    const variantId = url.searchParams.get(VARIANT_PARAM);
    url.searchParams.delete(MODEL_PARAM);
    url.searchParams.delete(VARIANT_PARAM);
    history.replaceState(history.state, '', url);

    const button = [...this.querySelectorAll('button[data-product-url]')].find(
      (option) => option instanceof HTMLButtonElement && option.dataset.productId === modelId
    );
    if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-pressed') === 'true') return;

    // Nur eine Variante übernehmen, die zu diesem Modell gehört
    const model = this.#models.get(modelId);
    if (variantId && model?.variants.some((variant) => String(variant.id) === variantId)) {
      button.dataset.variantId = variantId;
    }
    this.#select(button);
  }

  /**
   * Ist ein anderes Modell als das der Seite aktiv, bekommt das Warenkorbformular die versteckte
   * Eigenschaft mit dem Pfad der Seite, sonst wird sie entfernt. Das Modell steckt in der Variante der
   * Position. Muss nach jedem Austausch des Formulars laufen, weil morph() das Formular durch das des
   * geladenen Modells ersetzt.
   */
  #syncPageProperty() {
    const activeId = this.#activeProductId;
    const value = activeId && activeId !== this.dataset.pageProductId ? window.location.pathname : '';

    this.#section?.querySelectorAll('product-form-component form[data-type="add-to-cart-form"]').forEach((form) => {
      let input = form.querySelector(`input[name="${PAGE_PROPERTY}"]`);
      if (!value) {
        input?.remove();
        return;
      }
      if (!(input instanceof HTMLInputElement)) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = PAGE_PROPERTY;
        form.append(input);
      }
      input.value = value;
    });
  }

  #readModelData() {
    const script = this.querySelector('script[data-amq-model-data]');
    if (!script?.textContent) return;

    try {
      /** @type {ModelData[]} */
      const models = JSON.parse(script.textContent);
      this.#models = new Map(models.map((model) => [String(model.id), model]));
    } catch (error) {
      console.error('[amq-model-switcher] Variantendaten nicht lesbar', error);
    }
  }

  /**
   * Aktuelle Auswahl der Variantenauswahl als Optionsname → Wert
   * @returns {Map<string, string>}
   */
  #readSelection() {
    /** @type {Map<string, string>} */
    const selection = new Map();

    // Optionen mit nur einem Wert rendert der Variant-Picker als Text ohne Input. Ihr Wert steht
    // fest, also aus den Variantendaten des aktiven Modells übernehmen – sonst fiele z. B. die Länge
    // aus dem Abgleich heraus und beim Wechsel würde die erste Länge des anderen Modells gewählt.
    const activeModel = this.#models.get(this.#activeProductId ?? '');
    activeModel?.options.forEach((name, index) => {
      const values = new Set(activeModel.variants.map((variant) => variant.o[index]));
      if (values.size === 1) selection.set(name, [...values][0]);
    });

    const picker = this.#section?.querySelector('variant-picker');
    if (!picker) return selection;

    picker.querySelectorAll('fieldset input:checked').forEach((input) => {
      if (input instanceof HTMLInputElement && input.dataset.optionName) selection.set(input.dataset.optionName, input.value);
    });
    picker.querySelectorAll('select').forEach((select) => {
      const option = select.selectedOptions[0];
      if (option?.dataset.optionName) selection.set(option.dataset.optionName, option.value);
    });

    return selection;
  }

  /**
   * Findet im Modell die Variante zur aktuellen Auswahl. Verglichen werden nur die konfigurierten
   * Optionen (bzw. alle, die beide Produkte haben).
   * @param {ModelData} model
   * @param {Map<string, string>} selection
   * @returns {{ variant: ModelVariant | undefined, compared: boolean }}
   */
  #findVariant(model, selection) {
    const configured = (this.dataset.matchOptions ?? '')
      .split(',')
      .map((name) => name.trim())
      .filter(Boolean);
    const names = (configured.length ? configured : [...selection.keys()]).filter(
      (name) => selection.has(name) && model.options.includes(name)
    );
    if (!names.length) return { variant: undefined, compared: false };

    const variant = model.variants.find((candidate) =>
      names.every((name) => candidate.o[model.options.indexOf(name)] === selection.get(name))
    );
    return { variant, compared: true };
  }

  #updateAvailability() {
    if (!this.#models.size) return;

    const dataScript = this.querySelector('script[data-amq-model-data]');
    const unavailableText = dataScript?.getAttribute('data-unavailable-text') ?? '';
    const soldOutText = dataScript?.getAttribute('data-sold-out-text') ?? '';
    const selection = this.#readSelection();

    this.querySelectorAll('button[data-product-url]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      const model = this.#models.get(button.dataset.productId ?? '');
      if (!model) return;

      const isActive = button.getAttribute('aria-pressed') === 'true';
      const { variant, compared } = this.#findVariant(model, selection);
      const price = button.querySelector('.amq-model-switcher__price');
      const note = button.querySelector('.amq-model-switcher__note');

      // Aktives Modell nie sperren; ohne vergleichbare Optionen normal anzeigen
      const blockedText = isActive || !compared ? '' : !variant ? unavailableText : !variant.a ? soldOutText : '';

      button.disabled = Boolean(blockedText);
      if (variant && !blockedText) {
        button.dataset.variantId = String(variant.id);
      } else {
        delete button.dataset.variantId;
      }

      if (note instanceof HTMLElement) {
        note.textContent = blockedText;
        note.hidden = !blockedText;
      }
      // Farbkreise haben keinen sichtbaren Text, der Hinweis steht im Tooltip
      if (button.dataset.modelName) {
        button.title = blockedText ? `${button.dataset.modelName} – ${blockedText}` : button.dataset.modelName;
      }
      if (price instanceof HTMLElement) {
        price.hidden = Boolean(blockedText);
        price.textContent = variant && !blockedText ? variant.p : price.dataset.defaultPrice ?? price.textContent;
      }
    });
  }

  /** @param {Event} event */
  #onIntent = (event) => {
    const button = this.#getOptionButton(event.target);
    if (!button || button.disabled || button.getAttribute('aria-pressed') === 'true') return;
    const url = this.#buildRequestUrl(button);
    if (url) this.#load(url).catch(() => {});
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = this.#getOptionButton(event.target);
    if (!button || button.disabled || button.getAttribute('aria-pressed') === 'true') return;
    this.#select(button);
  };

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLButtonElement | null}
   */
  #getOptionButton(target) {
    const button = target instanceof Element ? target.closest('button[data-product-url]') : null;
    return button instanceof HTMLButtonElement ? button : null;
  }

  /**
   * Baut die Section-Rendering-URL. Nur Produkt-URLs des eigenen Shops sind erlaubt, damit nie
   * fremdes HTML in die Seite gemorpht wird.
   * @param {HTMLButtonElement} button
   * @returns {string | null}
   */
  #buildRequestUrl(button) {
    const { sectionId } = this.dataset;
    const { productUrl } = button.dataset;
    if (!sectionId || !productUrl) return null;

    const url = new URL(productUrl, window.location.origin);
    if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return null;

    url.search = '';
    url.hash = '';
    url.searchParams.set('section_id', sectionId);
    // Passende Variante zur aktuellen Auswahl direkt mitladen
    if (button.dataset.variantId) url.searchParams.set('variant', button.dataset.variantId);
    // Alternative Templates (?view=…) haben eigene Section-IDs, daher den Parameter mitgeben
    const view = new URL(window.location.href).searchParams.get('view');
    if (view) url.searchParams.set('view', view);

    return url.toString();
  }

  /**
   * Lädt eine Section (oder nimmt die vorgeladene aus dem Cache).
   * @param {string} url
   * @returns {Promise<string>}
   */
  #load(url) {
    const cached = this.#cache.get(url);
    if (cached && Date.now() - cached.time < CACHE_TTL_MS) return cached.text;

    const text = fetch(url).then((response) => {
      if (!response.ok) throw new Error(`Section Rendering API antwortete mit ${response.status}`);
      return response.text();
    });
    this.#cache.set(url, { time: Date.now(), text });
    text.catch(() => this.#cache.delete(url));
    return text;
  }

  /** @param {HTMLButtonElement} button */
  async #select(button) {
    const section = this.#section;
    const { sectionId } = this.dataset;
    const url = this.#buildRequestUrl(button);
    if (!section || !sectionId || !url) return;

    const requestId = ++this.#requestCount;
    const previous = this.querySelector('[aria-pressed="true"]');
    this.#setPressed(button);
    this.#showError(false);
    section.setAttribute('data-amq-model-loading', '');

    try {
      const [text] = await Promise.all([this.#load(url), this.#waitForVariantUpdates()]);
      // Inzwischen wurde ein anderes Modell gewählt
      if (requestId !== this.#requestCount) return;

      const html = new DOMParser().parseFromString(text, 'text/html');
      const newSection = html.getElementById(`shopify-section-${sectionId}`);
      if (!newSection) throw new Error('Section nicht in der Antwort gefunden');

      // Titel der Sticky-Leiste bleibt der des Seitenprodukts
      this.#restoreStickyTitle(newSection);

      // Soll-Zustand der Auswahl und gerenderte Variante aus dem frischen Server-HTML lesen,
      // bevor morph() die Knoten übernimmt
      const pickerState = this.#readPickerState(newSection);
      const variantId =
        newSection.querySelector('product-form-component input[name="id"]')?.getAttribute('value') ||
        button.dataset.variantId;

      for (const selector of SWAPPED_ELEMENTS) {
        const current = section.querySelectorAll(selector);
        const next = newSection.querySelectorAll(selector);
        current.forEach((element, index) => {
          const replacement = next[index];
          // Ganzes Element inkl. Attribute morphen, damit u. a. `data-product-id` zum neuen Modell passt
          if (replacement) morph(element, replacement, { ...MORPH_OPTIONS, childrenOnly: false });
        });
      }

      this.#applyPickerState(section, pickerState);
      this.#syncPageProperty();
      this.#updateAvailability();

      // URL folgt der Auswahl. Gehört die Variante zu einem anderen Modell als dem der Seite,
      // stellt #selectFromUrl sie beim Neuladen wieder her.
      const pageUrl = new URL(window.location.href);
      if (variantId) {
        pageUrl.searchParams.set('variant', variantId);
      } else {
        pageUrl.searchParams.delete('variant');
      }
      if (pageUrl.href !== window.location.href) history.replaceState(history.state, '', pageUrl);
    } catch (error) {
      if (requestId !== this.#requestCount) return;
      console.error('[amq-model-switcher]', error);
      // Auswahl zurücknehmen und Hinweis zeigen – die Seite bleibt unverändert
      if (previous) this.#setPressed(previous);
      this.#showError(true);
    } finally {
      if (requestId === this.#requestCount) {
        section.removeAttribute('data-amq-model-loading');
        document.documentElement.removeAttribute('data-amq-model-pending');
      }
    }
  }

  /**
   * morph() setzt bei Radios/Options, die nicht gewählt sein sollen, trotzdem das Attribut
   * (`updateAttribute` prüft `!= null`, und `false != null`). Danach stimmen Attribut und tatsächlicher
   * Zustand nicht mehr, und der Variant-Picker würde beim nächsten Wechsel eine falsche Auswahl
   * anfragen. Deshalb den Zustand vorher aus dem Server-HTML lesen und nachher gezielt setzen.
   * @param {Element} root
   * @returns {boolean[]} gewählt ja/nein je Radio bzw. Option, in Dokumentreihenfolge
   */
  #readPickerState(root) {
    return [...root.querySelectorAll(PICKER_CONTROLS)].map((control) =>
      control.hasAttribute(control instanceof HTMLOptionElement ? 'selected' : 'checked')
    );
  }

  /**
   * @param {Element} root
   * @param {boolean[]} state
   */
  #applyPickerState(root, state) {
    const controls = root.querySelectorAll(PICKER_CONTROLS);
    // Struktur passt nicht zum gelesenen Zustand → nichts verändern statt falsch zu setzen
    if (controls.length !== state.length) return;

    controls.forEach((control, index) => {
      const selected = Boolean(state[index]);
      if (control instanceof HTMLOptionElement) {
        control.selected = selected;
        control.toggleAttribute('selected', selected);
      } else if (control instanceof HTMLInputElement) {
        control.checked = selected;
        control.toggleAttribute('checked', selected);
        control.dataset.currentChecked = String(selected);
        control.dataset.previousChecked = 'false';
      }
    });
  }

  /** @param {boolean} visible */
  #showError(visible) {
    const error = this.querySelector('.amq-model-switcher__error');
    if (error instanceof HTMLElement) error.hidden = !visible;
  }

  /** @param {Element} button */
  #setPressed(button) {
    this.querySelectorAll('button[data-product-url]').forEach((option) => {
      option.setAttribute('aria-pressed', String(option === button));
    });

    // Farbkreise: Name der gewählten Farbe in der Überschrift („Farbe: Schwarz“)
    const selected = this.querySelector('.amq-model-switcher__selected');
    if (selected && button instanceof HTMLElement && button.dataset.modelName) {
      selected.textContent = `: ${button.dataset.modelName}`;
    }
  }
}

/**
 * @typedef {object} ModelVariant
 * @property {number} id
 * @property {boolean} a - verfügbar
 * @property {string} p - formatierter Preis
 * @property {string[]} o - Optionswerte in der Reihenfolge von `ModelData.options`
 */

/**
 * @typedef {object} ModelData
 * @property {number} id
 * @property {string[]} options - Optionsnamen
 * @property {ModelVariant[]} variants
 */

if (!customElements.get('amq-model-switcher')) {
  customElements.define('amq-model-switcher', AmqModelSwitcher);
}
